import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Smartphone, CheckCircle2, LogIn, ShieldAlert } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { auth } from "../lib/firebase";
import { BRAND } from "../lib/assets";

/* Mobile login handshake (WEB_BACKEND_HANDOFF §2b):
   GET /auth/mobile?redirect=<app callback> — runs web auth, then redirects back to the EXACT
   redirect value with ?token=<ACCESS_JWT>&refresh=<REFRESH_TOKEN>. Dynamic redirect supports
   both production (travelerpulse://) and Expo Go (exp://host/--/auth-callback) builds. */
const ALLOWED_SCHEMES = ["travelerpulse://", "exp://", "exp+travelerpulse://", "tpnativeapp://"];

export default function MobileAuthHandshake() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "";
  const [status, setStatus] = useState("idle"); // idle | working | ready | error
  const [callback, setCallback] = useState("");
  const [error, setError] = useState("");

  const redirectOk = useMemo(
    () => ALLOWED_SCHEMES.some((s) => redirect.toLowerCase().startsWith(s)), [redirect]);

  useEffect(() => { document.title = "Sign in to the TravelerPulse App"; }, []);

  useEffect(() => {
    if (loading || !user || !redirectOk || status !== "idle") return;
    setStatus("working");
    (async () => {
      try {
        const fbUser = auth?.currentUser;
        if (!fbUser) throw new Error("No active session");
        const token = await fbUser.getIdToken(true);
        const refresh = fbUser.refreshToken;
        const sep = redirect.includes("?") ? "&" : "?";
        const url = `${redirect}${sep}token=${encodeURIComponent(token)}&refresh=${encodeURIComponent(refresh)}`;
        setCallback(url);
        setStatus("ready");
        window.location.href = url; // hand the session back to the app
      } catch (e) {
        setError(e?.message || "Could not prepare your app session");
        setStatus("error");
      }
    })();
  }, [loading, user, redirectOk, status, redirect]);

  const loginNext = `/login?next=${encodeURIComponent(`/auth/mobile?redirect=${encodeURIComponent(redirect)}`)}`;

  return (
    <div className="min-h-[70vh] flex items-center justify-center bg-zinc-50 px-5" data-testid="mobile-auth-page">
      <div className="bg-white border border-zinc-300 p-8 max-w-md w-full text-center">
        <img src={BRAND.masthead} alt="TravelerPulse.pro" className="h-12 w-auto mx-auto mb-5 object-contain" />
        {!redirect || !redirectOk ? (
          <>
            <ShieldAlert className="w-10 h-10 mx-auto text-red-500 mb-3" />
            <h1 className="font-heading font-900 text-xl mb-2">Invalid app callback</h1>
            <p className="text-sm text-muted-foreground" data-testid="mobile-auth-bad-redirect">
              The redirect parameter is missing or uses a scheme that isn't allowed
              (travelerpulse://, exp://, exp+travelerpulse://).
            </p>
          </>
        ) : loading ? (
          <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" />
        ) : !user ? (
          <>
            <Smartphone className="w-10 h-10 mx-auto text-[#1a73e8] mb-3" />
            <h1 className="font-heading font-900 text-xl mb-2">Sign in to continue to the app</h1>
            <p className="text-sm text-muted-foreground mb-5">
              After signing in on the website we'll send your session straight back to the TravelerPulse app.
            </p>
            <Link to={loginNext} data-testid="mobile-auth-signin"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a73e8] text-white text-sm font-800 hover:opacity-90">
              <LogIn className="w-4 h-4" /> Sign in
            </Link>
          </>
        ) : status === "error" ? (
          <>
            <h1 className="font-heading font-900 text-xl mb-2 text-red-600">Something went wrong</h1>
            <p className="text-sm text-muted-foreground" data-testid="mobile-auth-error">{error}</p>
          </>
        ) : status === "ready" ? (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-[#34A853] mb-3" />
            <h1 className="font-heading font-900 text-xl mb-2">You're signed in!</h1>
            <p className="text-sm text-muted-foreground mb-5">
              Returning to the TravelerPulse app… if nothing happens, tap below.
            </p>
            <a href={callback} data-testid="mobile-auth-open-app"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#1a73e8] text-white text-sm font-800 hover:opacity-90">
              <Smartphone className="w-4 h-4" /> Open the app
            </a>
          </>
        ) : (
          <>
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-[#1a73e8] mb-3" />
            <p className="text-sm text-muted-foreground">Preparing your app session…</p>
          </>
        )}
      </div>
    </div>
  );
}
